import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnInit, LOCALE_ID } from '@angular/core';
import { TripSlas } from '../../models/tripSlas';
import { TripDashboardDataService } from '../../services/trip-dashboard-data.service';
import { TripDashboardTile } from '../../models/tripDashboardTile';
import { HighchartOptionsService } from '../../services/highchart-options.service';
import { TranslateService } from '@ngx-translate/core';
import { DbKeyValueDecimal } from '../../models/dbKeyValueDecimal';
import { TelecomOperator } from '../../models/telecomOperator';
import * as Highcharts from 'highcharts';
import HC_exporting from "highcharts/modules/exporting";
import { DecimalPipe, formatNumber } from '@angular/common';
import { Subscription } from 'rxjs';
HC_exporting(Highcharts);
@Component({
  selector: 'app-trip-dashboard-stats',
  template: require('./trip-dashboard-stats.component.html'),
  styles: [require('./trip-dashboard-stats.component.sass')],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripDashboardStatsComponent implements OnInit, OnChanges {

  Highcharts: typeof Highcharts = Highcharts; // required

  @Input() tripSlas: TripSlas;
  @Input() operators: TelecomOperator[];
  @Input() operatorConsumptions: DbKeyValueDecimal[];
  @Input() exceededUsers: DbKeyValueDecimal[];
  @Input() spreadUsers: DbKeyValueDecimal[];
  @Input() classAvgConsumptions: DbKeyValueDecimal[];
  @Input() operatorDailyPercentageConsumptions: DbKeyValueDecimal[];
  @Input() operatorDailyTotalConsumptions: DbKeyValueDecimal[];
  @Input() modemSpectrumBandUsages: DbKeyValueDecimal[];
  @Input() dailyTotalUserSpreads: DbKeyValueDecimal[];
  @Input() dailyPercentageUserSpreads: DbKeyValueDecimal[];
  @Input() classTotalConsumptions: DbKeyValueDecimal[];
  @Input() dailyQoEs: DbKeyValueDecimal[];

  tiles: TripDashboardTile[];
  
  consumptionPerOperatorChartOptions: Highcharts.Options;
  fairUseExceedPerClassChartOptions: Highcharts.Options;
  userSpreadPerClassChartOptions: Highcharts.Options;
  avgConsumptionPerClassChartOptions: Highcharts.Options;
  dailyConsumptionPerOperatorChartOptions: Highcharts.Options;
  dailyConsumptionPerOperatorPercentageChartOptions: Highcharts.Options;
  spectrumBandPerModemUsageChartOptions: Highcharts.Options;
  avgHourlyUsersChartOptions:  Highcharts.Options;
  dailyUserSpreadPerClassChartOptions: Highcharts.Options;
  dailyUserSpreadPerClassPercentageChartOptions: Highcharts.Options;
  dailyAvgConsumptionPerClassChartOptions: Highcharts.Options;
  qoeChartOptions: Highcharts.Options;
  tripNumberChartOptions: Highcharts.Options;

  languageChangeSubscription: Subscription;

  constructor(
    private tripDashboardDataService: TripDashboardDataService,
    private translateService: TranslateService,
    private highchartOptionsService: HighchartOptionsService,
    @Inject(LOCALE_ID) private locale: string
  ) { 
  
  }

  ngOnInit(): void {
    this.languageChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this._generateCharts(true);
    })
  }

  ngOnChanges(): void {
    this._generateCharts();
  }

  _generateCharts = (redrawChart?: boolean) => {
    this.tiles = this.tripSlas ? this.tripDashboardDataService.getTiles(this.tripSlas, this.operatorConsumptions) : [];

    if ((!this.consumptionPerOperatorChartOptions || redrawChart) && this.operatorConsumptions) {
      const chartContent = this.tripDashboardDataService.processOperatorConsumption(this.operatorConsumptions, this.operators);
      this.consumptionPerOperatorChartOptions  = this.highchartOptionsService.pieChart(
        chartContent.data,
        chartContent.colors,
        this.translateService.instant("I18N_DASHBOARD.CHART_CONSUMPTION_PER_OPERATOR"),
        this.translateService.instant("I18N_DASHBOARD.CHART_CONSUMPTION_PER_OPERATOR_DESC"),
        '<b>{point.name}</b>: {point.percentage:.1f} % <br> {point.label} </br>',
        '<br> {point.label} </br>'
      )
    }

    if((!this.fairUseExceedPerClassChartOptions || redrawChart) && this.tripSlas && this.exceededUsers) {
      const chartContent = this.tripDashboardDataService.processExceededUsers(this.exceededUsers, this.operators, this.tripSlas.portalServiceClassNames);
      this.fairUseExceedPerClassChartOptions = this.highchartOptionsService.pieChart(
        chartContent.data,
        chartContent.colors,
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_FAIR_USE_EXCEED_PER_CLASS"),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_FAIR_USE_EXCEED_PER_CLASS_DESC"),
        '<b>{point.name}</b>: {point.y} ' + this.translateService.instant("I18N_DASHBOARD.USERS") + '</br>',
        '<br> {point.label} </br>'
      )
    }

    if((!this.userSpreadPerClassChartOptions || redrawChart) && this.tripSlas && this.spreadUsers) {
      const chartContent = this.tripDashboardDataService.processSpreadUsers(this.spreadUsers, this.operators, this.tripSlas.portalServiceClassNames);
      this.userSpreadPerClassChartOptions = this.highchartOptionsService.pieChart(
        chartContent.data,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_USER_SPREAD_PER_CLASS"),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_USER_SPREAD_PER_CLASS_DESC"),
        '<b>{point.name}</b>: {point.percentage:.1f} % <br>{point.y} ' + this.translateService.instant("I18N_DASHBOARD.USERS") + '</br> ',
        '<b>{point.name}</b>: {point.y} ' + this.translateService.instant("I18N_DASHBOARD.USERS") + '</br>'
      )
    }

  
    if((!this.avgConsumptionPerClassChartOptions || redrawChart) && this.tripSlas && this.classAvgConsumptions) {
      const chartContent = this.tripDashboardDataService.processClassAvgConsumptions(this.classAvgConsumptions, this.operators, this.tripSlas.portalServiceClassNames);
      this.avgConsumptionPerClassChartOptions = this.highchartOptionsService.pieChart(
        chartContent.data,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_AVG_CONSUMPTION_PER_CLASS"),
        this.translateService.instant("I18N_DASHBOARD.CHART_AVG_CONSUMPTION_PER_CLASS_DESC"),
        '<b>{point.name}</b>: {point.y} MB</br> ',
        '<b>{point.name}</b>: {point.y} MB</br>'
      )
    }

    if((!this.dailyConsumptionPerOperatorChartOptions || redrawChart) && this.tripSlas && this.operatorDailyTotalConsumptions) {
      const chartContent = this.tripDashboardDataService.processChartData(this.operatorDailyTotalConsumptions, this.operators, this.tripSlas.portalServiceClassNames,  (value: DbKeyValueDecimal) => Math.round(value.dbValue/ (1024 * 1024)));
      this.dailyConsumptionPerOperatorChartOptions = this.highchartOptionsService.columnChart(
        chartContent.data,
        chartContent.categories,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_CONSUMPTION_PER_OPERATOR"),
        '',
        '<b>{name}</br> ',
        '{series.name}: '+ '<b>{point.y}  </b><br/>',
        true,
        true,
        '',
        {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f}",
            },
          },
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f}",
            },
          },
        }
      )
    }

   
    if((!this.dailyConsumptionPerOperatorPercentageChartOptions || redrawChart) && this.tripSlas && this.operatorDailyPercentageConsumptions) {
      const chartContent = this.tripDashboardDataService.processChartData(this.operatorDailyPercentageConsumptions, this.operators, this.tripSlas.portalServiceClassNames,  (value: DbKeyValueDecimal) => parseFloat(formatNumber(value.dbValue * 100, this.locale, "1.2-2")));
      this.dailyConsumptionPerOperatorPercentageChartOptions = this.highchartOptionsService.columnChart(
        chartContent.data,
        chartContent.categories,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_PERCENT_DAILY_CONSUMPTION_PER_OPERATOR"),
        '{point.y:,.1f}',
        '',
        '{series.name}: '+ '<b>{point.y} % </b><br/>',
        true,
        true,
        '',
        {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} %",
            },
          },
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} %",
            },
          },
        }
      )
  
    }


    if((!this.spectrumBandPerModemUsageChartOptions || redrawChart) && this.tripSlas && this.modemSpectrumBandUsages) {
       const xAxisLabels = this.modemSpectrumBandUsages.reduce((acc: string[], item) => {
            const modem = item.dbKey.split('_')[0].replace('-Cell-', 'M');
            if (!acc.find(x => x === modem)) {
                acc.push(modem)
            }
            return acc;
        }, []);

        const series = this.modemSpectrumBandUsages.reduce((acc, item) => {
            const modem = item.dbKey.split('_')[0].replace('-Cell-', 'M');
            var spectrumBand = item.dbKey.split('_')[1];
            if (!acc[spectrumBand]) {
                acc[spectrumBand] = xAxisLabels.map(() => null);
            }
            acc[spectrumBand][xAxisLabels.indexOf(modem)] = item.dbValue;
            return acc;
        }, {})

        const dataset = Object.keys(series).map(key => {
            return {
                name: key,
                data: series[key]
            }
        });

      const chartContent = this.tripDashboardDataService.processChartData(this.modemSpectrumBandUsages, this.operators, this.tripSlas.portalServiceClassNames);
      this.spectrumBandPerModemUsageChartOptions = this.highchartOptionsService.columnChart(
        dataset,
        chartContent.categories,
        Highcharts.getOptions().colors,
        this.translateService.instant("I18N_DASHBOARD.CHART_SPECTRUM_BAND_USAGE_PER_MODEM"),
        '',
        '<b>{name}</br> ',
        '{series.name}: '+ '<b>{point.y} % </b><br/>',
        true,
        true,
        '',
        {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} %",
            },
          },
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} %",
            },
          },
        }
      )
    }

      
    if((!this.avgHourlyUsersChartOptions || redrawChart) && this.tripSlas && this.tripSlas.uniqueUsersPerDay) {
      const chartContent = this.tripDashboardDataService.processChartData(this.tripSlas.uniqueUsersPerDay, this.operators, this.tripSlas.portalServiceClassNames);
      this.avgHourlyUsersChartOptions = this.highchartOptionsService.columnChart(
        chartContent.data,
        chartContent.categories,
        Highcharts.getOptions().colors,
        this.translateService.instant("I18N_DASHBOARD.CHART_TOTAL_INTERNET_USERS_PER_DAY"),
        '',
        '<b>{name}</br> ',
        this.translateService.instant("I18N_DASHBOARD.USERS") + ': '+ '<b>{point.y}</b><br/>',
        false,
        false,
        this.translateService.instant("I18N_DASHBOARD.USERS")
      )
    }

    
    if((!this.dailyUserSpreadPerClassChartOptions || redrawChart) && this.dailyTotalUserSpreads) {
      const chartContent = this.tripDashboardDataService.processChartData(this.dailyTotalUserSpreads, this.operators, this.tripSlas.portalServiceClassNames, null , this.tripDashboardDataService.replaceClassIdByName);
      this.dailyUserSpreadPerClassChartOptions = this.highchartOptionsService.columnChart(
        chartContent.data,
        chartContent.categories,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_USERS_PER_CLASS"),
        '',
        '<b>{name}</br> ',
        '',
        true,
        true,
        '',
        {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
              format: "{point.y}",
            },
          },
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y}",
            },
          },
        }
      )
    }

  

    if((!this.dailyUserSpreadPerClassPercentageChartOptions || redrawChart) && this.dailyPercentageUserSpreads) {
      const chartContent = this.tripDashboardDataService.processChartData(this.dailyPercentageUserSpreads, this.operators, this.tripSlas.portalServiceClassNames, (value) => value.dbValue * 100 , this.tripDashboardDataService.replaceClassIdByName);
      this.dailyUserSpreadPerClassPercentageChartOptions = this.highchartOptionsService.columnChart(
        chartContent.data,
        chartContent.categories,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_PERCENT_USERS_PER_CLASS"),
        '',
        '<b>{name}</br> ',
        '{series.name}: '+ '<b>{point.y} % </b><br/>',
        true,
        true,
        '',
        {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} %",
            },
          },
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} %",
            },
          },
        }
      )
    }


    if((!this.dailyAvgConsumptionPerClassChartOptions || redrawChart) && this.classTotalConsumptions) {
      const chartContent = this.tripDashboardDataService.processChartData(this.classTotalConsumptions, this.operators, this.tripSlas.portalServiceClassNames,  (value: DbKeyValueDecimal) => parseFloat(formatNumber(value.dbValue / (1024 * 1024), this.locale, "1.2-2")), this.tripDashboardDataService.replaceClassIdByName);
      this.dailyAvgConsumptionPerClassChartOptions = this.highchartOptionsService.lineChart(
        chartContent.data,
        chartContent.categories,
        chartContent.colors.filter(color => !!color),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_CONSUMPTION_PER_CLASS"),
        '',
        '<b>{name}</br> ',
        '{series.name}: '+ '<b>{point.y} MB </b><br/>',
        false,
        true,
        '',
        {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} MB",
            },
          },
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y:,.2f} MB",
            },
          },
        }
      )
    }



    if(!this.qoeChartOptions && this.dailyQoEs && this.tripSlas && this.tripSlas.dashboardConfiguration && !this.tripSlas.dashboardConfiguration.dashboardHideQoEChart) {
      const chartContent = this.tripDashboardDataService.processQoEChartData(this.dailyQoEs, Highcharts.getOptions().colors);
      this.qoeChartOptions = this.highchartOptionsService.lineQoSChart(
        chartContent.data,
        chartContent.categories,
        Highcharts.getOptions().colors,
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_QOS_INDICATORS"),
        '',
        '<b>{name}</br> ',
        '',
        false,
        true,
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_QOS_AXIS_THROUGHPUT"),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_QOS_AXIS_DOWNLOAD_TIME"),
        this.translateService.instant("I18N_DASHBOARD.CHART_DAILY_QOS_AXIS_LATENCY")
      )
    }

    if((!this.tripNumberChartOptions || redrawChart) && this.tripSlas && this.tripSlas.tripCountPerTrain ) {
      this.tripSlas.tripCountPerTrain = this.tripSlas.tripCountPerTrain.sort((t1, t2) => parseInt(t1.dbKey) > parseInt(t2.dbKey) ? 1 : -1);
      this.tripNumberChartOptions = this.highchartOptionsService.columnChart(
        [{ data: this.tripSlas.tripCountPerTrain.map(train => train.dbValue)}],
        [...new Set(this.tripSlas.tripCountPerTrain.map(train => train.dbKey))],
        Highcharts.getOptions().colors,
        this.translateService.instant("I18N_DASHBOARD.CHART_TOTAL_TRIPS_PER_TRAIN"),
        '',
        `<b>${this.translateService.instant("I18N_TRIP.TRAIN")} {name}</br>`,
        '',
        false,
        false,
        this.translateService.instant("I18N_DASHBOARD.CHART_TOTAL_TRIPS_PER_TRAIN_YAXIS")
      )
    }
    
  }

}
